import React, { useMemo } from 'react';
import Image from '../../../components/AppImage';
import StatusPill from '../../../components/ui/StatusPill';
import Icon from '../../../components/AppIcon';

// Card compacto para uso na calculadora, com estilo idêntico ao PrinterCard
// Exibe: Uso relativo, Break-even, Dias ativos, Jobs realizados
const PrinterSummaryCard = ({ printer, selected = false, onSelect, usageSharePct = 0 }) => {
  const status = printer?.status || 'idle';
  const thumb = Array.isArray(printer?.image_urls) && printer?.image_urls?.length > 0
    ? printer?.image_urls?.[0]
    : (printer?.image || printer?.image_url);

  // Normaliza campos extras (AMS, Bico)
  const hasAMS = typeof printer?.hasAMS !== 'undefined' ? printer?.hasAMS : printer?.has_ams;
  const nozzleSize = printer?.nozzleSize ?? printer?.nozzle_size;

  // Métricas (com cálculos/fallbacks semelhantes ao PrinterCard)
  const utilization = Number(printer?.utilization || 0);
  const jobsData = Array.isArray(printer?.jobs) ? printer?.jobs : [];
  const jobsCount = jobsData?.length > 0 ? jobsData?.length : Number(printer?.completedJobs || 0) || 0;
  const totalHours = Number(printer?.totalHours || 0);
  const jobHours = (j) => Number(j?.duration_hours || j?.estimated_duration_hours || j?.durationHours || j?.hours || 0);
  const totalHoursUsed = jobsData?.length > 0 ? (jobsData?.reduce((sum, j) => sum + jobHours(j), 0) || 0) : totalHours;
  const purchasePrice = (() => {
    const raw = (printer?.purchasePrice ?? printer?.purchase_cost ?? printer?.purchaseCost ?? '').toString();
    const num = Number(raw.replace(/[^0-9.,-]/g, '').replace('.', '').replace(',', '.'));
    return isNaN(num) ? 0 : num;
  })();
  const revenueLifetime = jobsData?.length > 0
    ? jobsData?.reduce((sum, j) => sum + (Number(j?.price || j?.revenue || 0)), 0)
    : Number(printer?.totalRevenue || 0);
  const breakEvenProgress = purchasePrice > 0 ? Math.max(0, Math.min(100, (revenueLifetime / purchasePrice) * 100)) : 0;
  const isPaidOff = purchasePrice > 0 ? revenueLifetime >= purchasePrice : false;

  // Dias ativos (estimativa simples com base na utilização se não houver logs)
  const operationBasisDays = 30;
  const utilPctBase = Math.max(0, Math.min(100, Number(utilization || 0)));
  const activeDaysTri = Math.max(0, Math.round((utilPctBase / 100) * operationBasisDays));
  const activePctTri = operationBasisDays > 0 ? (activeDaysTri / operationBasisDays) * 100 : 0;

  // Uso relativo no conjunto
  const usagePct = Math.max(0, Math.min(100, Number(usageSharePct || 0)));
  const usageLabel = usagePct <= 33 ? 'Uso baixo no conjunto' : usagePct <= 66 ? 'Uso médio no conjunto' : 'Uso alto no conjunto';
  const usageColorCls = usagePct <= 33 ? 'text-success' : usagePct <= 66 ? 'text-warning' : 'text-error';
  const usageBarCls = usagePct <= 33 ? 'bg-success' : usagePct <= 66 ? 'bg-warning' : 'bg-error';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(printer?.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect?.(printer?.id); }}
      className={`relative rounded-xl bg-card border ${selected ? 'border-primary bg-primary/5' : 'border-border'} shadow-none overflow-hidden transition-all hover:shadow-xs cursor-pointer`}
    >
      {/* Badges à direita: marca e status (somente visual) */}
      <div className="absolute top-4 right-4 flex flex-col items-end gap-1 z-10">
        {(printer?.brand || printer?.manufacturer) && (
          <span
            className="text-xs px-3 py-1 rounded-full bg-black/50 text-white border border-white/30 shadow-xs backdrop-blur-sm"
            title={printer?.brand || printer?.manufacturer}
          >
            {printer?.brand || printer?.manufacturer}
          </span>
        )}
        <span className="inline-flex rounded-full bg-black/30 border border-white/20 p-0.5 backdrop-blur-sm">
          <StatusPill status={status} />
        </span>
      </div>

      {/* Foto grande */}
      <div className="w-full h-40 sm:h-48 md:h-52 bg-muted overflow-hidden">
        {thumb ? (
          <Image src={thumb} alt={printer?.imageAlt || printer?.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="Printer" size={56} className="text-muted-foreground/50" />
          </div>
        )}
      </div>

      {/* Badges de AMS e Bico */}
      <div className="px-5 pt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-2.5 py-1 text-xs rounded-md bg-muted/40 border border-border/60 text-foreground" title="Sistema de gerenciamento automático de materiais">
            AMS: {typeof hasAMS === 'boolean' ? (hasAMS ? 'Sim' : 'Não') : '—'}
          </span>
          <span className="px-2.5 py-1 text-xs rounded-md bg-muted/40 border border-border/60 text-foreground" title="Diâmetro do bico instalado">
            Bico: {nozzleSize ? `${nozzleSize} mm` : '—'}
          </span>
        </div>
      </div>

      {/* Cabeçalho */}
      <header className="px-5 pt-4 pb-3">
        <div className="min-w-0">
          <span className="text-xs text-muted-foreground" title={printer?.model || 'Modelo não informado'}>{printer?.model || 'Modelo não informado'}</span>
          <h3 className="text-base font-medium text-foreground mt-1" title={printer?.name || printer?.model || 'Impressora'}>
            {printer?.name || 'Impressora'}
          </h3>
        </div>
      </header>

      {/* Conteúdo resumido */}
      <div className="px-5 pb-5">
        {/* Uso relativo */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Uso relativo</span>
            <span className="text-sm font-medium text-foreground">{totalHoursUsed}h</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div className={`${usageBarCls} h-2 rounded-full transition-all`} style={{ width: `${Math.max(usagePct, 5)}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className={`${usageColorCls} text-xs font-medium`}>{usageLabel}</span>
            <span className="text-xs text-muted-foreground">{usagePct.toFixed(1)}%</span>
          </div>
        </div>

        {/* Break-even */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Break-even</span>
            <span className="text-sm font-medium text-foreground">{breakEvenProgress.toFixed(0)}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div className={`bg-primary h-2 rounded-full transition-all`} style={{ width: `${Math.max(breakEvenProgress, 3)}%` }} />
          </div>
          <div className="text-xs text-muted-foreground mt-2">
            {isPaidOff ? 'Pago' : 'Ainda não se pagou'}
          </div>
        </div>

        {/* Dias ativos + Jobs realizados */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-xl bg-muted/30 min-w-0">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
              <Icon name="Calendar" size={14} />
              <span>Dias ativos (estimado)</span>
            </div>
            <div className="text-sm font-semibold text-foreground">{activeDaysTri > 0 ? activeDaysTri : '—'}</div>
          </div>
          <div className="p-3 rounded-xl bg-muted/30 min-w-0">
            <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
              <Icon name="ListChecks" size={14} />
              <span>Jobs realizados</span>
            </div>
            <div className="text-sm font-semibold text-foreground">{jobsCount > 0 ? jobsCount : '—'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterSummaryCard;